import React, { useRef, useState } from "react";
import { uploadImage, apiErr, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Star, Trash2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

export default function PhotoManager({ images = [], mainImage = "", onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) urls.push(await uploadImage(f));
      const next = [...images, ...urls];
      onChange({ images: next, main_image: mainImage || next[0] });
      toast.success(`${urls.length} photo(s) ajoutée(s)`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const remove = (url) => {
    const next = images.filter((u) => u !== url);
    onChange({ images: next, main_image: mainImage === url ? (next[0] || "") : mainImage });
  };

  const setMain = (url) => onChange({ images, main_image: url });

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ images: next, main_image: mainImage });
  };

  return (
    <div>
      <span className="text-xs font-medium text-ink-600 mb-1 block">Photos</span>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="cursor-pointer rounded-xl border-2 border-dashed border-brand/40 bg-brand-50/50 p-6 text-center hover:bg-brand-50 transition-colors mb-3"
        data-testid="photo-dropzone"
      >
        {busy ? <Loader2 className="mx-auto animate-spin text-brand" /> : <Upload className="mx-auto text-brand mb-1" size={22} />}
        <p className="text-sm text-ink-600">Glissez-déposez vos photos ici ou cliquez pour sélectionner</p>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="photo-input" />
      </div>
      {images.length > 0 && (
        <>
          <p className="text-xs text-ink-400 mb-2">Utilisez les flèches pour réordonner les photos · l'étoile définit la photo principale.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((url, idx) => (
              <div key={url} className={`group relative rounded-lg overflow-hidden border-2 ${mainImage === url ? "border-brand" : "border-transparent"}`} data-testid={`photo-item-${idx}`}>
                <img src={mediaUrl(url)} alt="" className="w-full aspect-square object-cover" />
                <span className="absolute top-1 right-1 rounded-full bg-black/60 text-white text-[9px] px-1.5 py-0.5 pointer-events-none">{idx + 1}</span>
                <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" onClick={() => setMain(url)} title="Photo principale" className="p-1.5 rounded-full bg-white text-brand shadow" data-testid={`photo-main-${idx}`}><Star size={13} className={mainImage === url ? "fill-brand" : ""} /></button>
                  <button type="button" onClick={() => remove(url)} title="Supprimer" className="p-1.5 rounded-full bg-white text-red-500 shadow" data-testid={`photo-delete-${idx}`}><Trash2 size={13} /></button>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-1 py-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} title="Déplacer à gauche" className="p-1 rounded-full bg-white text-brand shadow disabled:opacity-30" data-testid={`photo-left-${idx}`}><ChevronLeft size={13} /></button>
                  <button type="button" disabled={idx === images.length - 1} onClick={() => move(idx, 1)} title="Déplacer à droite" className="p-1 rounded-full bg-white text-brand shadow disabled:opacity-30" data-testid={`photo-right-${idx}`}><ChevronRight size={13} /></button>
                </div>
                {mainImage === url && <span className="absolute bottom-1 left-1 rounded-full bg-brand text-white text-[9px] px-1.5 py-0.5 pointer-events-none group-hover:opacity-0 transition-opacity">Principale</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

